import type { Engine } from "../../engine/Engine";
import {
  ElementEventPlatform,
  type DesignerElementEventChannels,
} from "./ElementEventPlatform";

/**
 * Guarantees a designer class always has typed channels to emit on.
 *
 * Before this, every designer class carried an optional
 * `_events?: DesignerElementEventChannels` and each call site was written twice:
 * once against the channel, once against `engine.dispatchCustomEvent` as a
 * fallback. The fallback was real — `ConfiguratorCapabilityElements` still
 * constructs `DesignerConfiguratorBridge` without channels — so the branches
 * could not simply be deleted.
 *
 * Instead, when no channels are supplied we own a private
 * `ElementEventPlatform`. Its `bindLegacyEngine` bridge is bidirectional, so
 * legacy engine listeners keep receiving everything they used to while the
 * class itself has a single, typed emit path.
 */
export class DesignerChannels {
  private readonly _own?: ElementEventPlatform;
  readonly channels: DesignerElementEventChannels;

  constructor(provided?: DesignerElementEventChannels) {
    if (provided) {
      this.channels = provided;
      return;
    }
    this._own = new ElementEventPlatform();
    this.channels = this._own.designer;
  }

  /** True when these channels are private (no viewport platform supplied). */
  get isOwned(): boolean {
    return this._own !== undefined;
  }

  /** No-op when the viewport supplied the channels — it owns the bridge then. */
  bindEngine(engine: Engine | undefined): void {
    if (engine) this._own?.bindLegacyEngine(engine);
  }

  dispose(): void {
    this._own?.unbindLegacyEngine();
  }
}
